/**
 * /auth/callback — handles the redirect from Cognito Hosted UI.
 *
 * 1. Reads ?code=, ?state=, ?error= from the URL
 * 2. Validates state against the value stashed in sessionStorage at login()
 * 3. Exchanges the code (+ PKCE verifier) for tokens
 * 4. Hands tokens to AuthContext and navigates back to the originally-requested path
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useAuth } from './AuthContext'
import { exchangeCodeForTokens } from './oauth'
import { readAndClearPkce } from './storage'

export default function AuthCallback() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { acceptTokens } = useAuth()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const code = params.get('code')
    const state = params.get('state')
    const oauthError = params.get('error')

    if (oauthError) {
      setError(`Sign-in failed: ${oauthError}${params.get('error_description') ? ` — ${params.get('error_description')}` : ''}`)
      return
    }
    if (!code) {
      setError('Missing authorization code in callback URL.')
      return
    }

    const stash = readAndClearPkce()
    if (!stash.verifier) {
      setError('Sign-in session expired (no PKCE verifier). Please try again.')
      return
    }
    if (state && stash.state && state !== stash.state) {
      setError('Sign-in failed: state mismatch (possible CSRF).')
      return
    }

    exchangeCodeForTokens({ code, codeVerifier: stash.verifier })
      .then((resp) => {
        acceptTokens({
          idToken: resp.id_token,
          refreshToken: resp.refresh_token ?? null,
          expiresAt: Math.floor(Date.now() / 1000) + resp.expires_in,
        })
        navigate(stash.postLoginPath, { replace: true })
      })
      .catch((e: Error) => setError(e.message))
  }, [params, acceptTokens, navigate])

  if (error) {
    return (
      <div style={{ padding: '2rem', maxWidth: 600, margin: '0 auto', fontFamily: 'system-ui, sans-serif' }}>
        <h1 style={{ color: '#b91c1c' }}>Sign-in error</h1>
        <p>{error}</p>
        <button
          onClick={() => navigate('/', { replace: true })}
          style={{ padding: '0.5rem 1rem', borderRadius: 6, border: 'none', background: '#2563eb', color: 'white', cursor: 'pointer' }}
        >
          Try again
        </button>
      </div>
    )
  }

  return (
    <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui, sans-serif', color: '#6b7280' }}>
      Signing you in…
    </div>
  )
}
